/**
 * Drone Operator Coverage layer (L.2.2, §3B.6). Each provider renders as a base
 * marker (ambient quadcopter glyph) PLUS a coverage circle = the asset's service
 * radius (#343 unified marketplace fleet). Phase 6 legibility: the fill is a very
 * faint overlap-budgeted wash (α0.07) and the OUTLINE carries the edge, so stacked
 * bubbles never blob and ambient context (communities/closures/assets) reads over
 * the coverage. When an incident is selected and a provider can reach it, that
 * provider's coverage HIGHLIGHTS (brighter fill + solid outline) — the COP answers
 * reachability visually.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh / setVisible /
 * pick → DroneCoverageFeature → the Drone Coverage detail panel. setSelectedIncident
 * re-paints the reachability highlight when the operator selects an incident.
 */
import * as Cesium from 'cesium';
import {
  fetchDroneCoverage,
  type BboxQuery,
  type DroneCoverageFeature,
  type DroneCoverageFeatureCollection,
} from '../api';
import {
  DRONE_AMBIENT_COLOR,
  DRONE_COVERAGE_ALPHA,
  DRONE_COVERAGE_HIGHLIGHT_MULT,
  DRONE_GLYPH_SVG,
  canReach,
  displayCoverageRadiusKm,
  geodesicRingDegrees,
  isCoverageOverProvisioned,
} from '../droneCoverage';
import { tintedSvgDataUrl } from '../svgIcon';
import { DRONE_COVERAGE_FILL_PREFIX } from '../coveragePick';

const REFRESH_DEBOUNCE_MS = 300;
const DRONE_PREFIX = 'drone:';

export interface DroneCoverageLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Current selected incident point (lat/lon), for the reachability highlight. */
  getSelectedIncident?: () => { lat: number; lon: number } | null;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<DroneCoverageFeatureCollection>;
}

export interface DroneCoverageLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Re-paint coverage highlights against a newly-selected incident (or null). */
  setSelectedIncident(incident: { lat: number; lon: number } | null): void;
  pick(entity: unknown): DroneCoverageFeature | null;
  destroy(): void;
}

export function installDroneCoverageLayer(
  viewer: Cesium.Viewer,
  opts: DroneCoverageLayerOptions = {},
): DroneCoverageLayerHandle {
  const fetcher = opts.fetcher ?? fetchDroneCoverage;
  const ds = new Cesium.CustomDataSource('drone-coverage');
  void viewer.dataSources.add(ds);
  const glyphUrl = tintedSvgDataUrl('drone-glyph', DRONE_GLYPH_SVG, DRONE_AMBIENT_COLOR);

  const entityToFeature = new Map<Cesium.Entity, DroneCoverageFeature>();
  let lastFc: DroneCoverageFeatureCollection | null = null;
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const baseColor = Cesium.Color.fromCssColorString(DRONE_AMBIENT_COLOR);

  const render = (fc: DroneCoverageFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    const incident = opts.getSelectedIncident?.() ?? null;

    for (const feature of fc.features) {
      const serviceRadiusKm = feature.properties.service_radius_km;
      const [lon, lat] = feature.geometry.coordinates;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      const reachable = incident
        ? canReach(serviceRadiusKm, lat, lon, incident.lat, incident.lon)
        : false;

      // Coverage circle. The rendered radius is the asset's service radius CLAMPED to the
      // realistic BVLOS display cap (#374) — a stated 400 km radius paints a 50 km bubble, so
      // the map never overstates a single-sortie reach. Added first → renders beneath the base
      // marker. Highlight when the selected incident is reachable (reachability still reads the
      // operator's TRUE stated radius — display-only cap).
      const displayRadiusKm = displayCoverageRadiusKm(serviceRadiusKm);
      const radiusM = displayRadiusKm * 1000;
      const overProvisioned = isCoverageOverProvisioned(serviceRadiusKm);
      if (radiusM > 0) {
        const ellipse = ds.entities.add({
          id: `${DRONE_COVERAGE_FILL_PREFIX}${feature.id}`,
          position,
          ellipse: {
            semiMajorAxis: radiusM,
            semiMinorAxis: radiusM,
            material: baseColor.withAlpha(
              reachable ? DRONE_COVERAGE_ALPHA * DRONE_COVERAGE_HIGHLIGHT_MULT : DRONE_COVERAGE_ALPHA,
            ),
            // Over-provisioned bubbles get a DASHED boundary ring (below) instead of the solid
            // outline, signalling the edge is a display cap, not the operator's stated range.
            outline: !overProvisioned,
            // Phase 6 legibility — the OUTLINE is now the primary read. With the resting fill
            // dropped to α0.07, the ring carries "where the edge is", so the resting outline is
            // weighted heavier (1 → 1.5 px) and higher-contrast (α0.4 → 0.5). The reachable
            // ring (bright solid, 2 px) still wins.
            outlineColor: baseColor.withAlpha(reachable ? 0.9 : 0.5),
            outlineWidth: reachable ? 2 : 1.5,
            height: 0,
          },
        });
        entityToFeature.set(ellipse, feature);

        if (overProvisioned) {
          // Dashed "≤50 km BVLOS display cap" ring (mirrors the SectorsLayer dashed-boundary
          // idiom) — marks the bubble as a capped subset of an aspirational stated radius.
          const ring = ds.entities.add({
            id: `${DRONE_COVERAGE_FILL_PREFIX}${feature.id}:cap`,
            polyline: {
              positions: Cesium.Cartesian3.fromDegreesArray(
                geodesicRingDegrees(lat, lon, displayRadiusKm),
              ),
              width: reachable ? 2 : 1.5,
              material: new Cesium.PolylineDashMaterialProperty({
                color: baseColor.withAlpha(reachable ? 0.9 : 0.55),
                dashLength: 10,
              }),
              clampToGround: true,
            },
          });
          // The ring shares the bubble's feature so a click on the dashed edge still opens
          // the coverage panel (same as the fill).
          entityToFeature.set(ring, feature);
        }
      }

      const marker = ds.entities.add({
        id: `${DRONE_PREFIX}${feature.id}`,
        position,
        billboard: {
          image: glyphUrl,
          width: 22,
          height: 22,
          color: Cesium.Color.WHITE.withAlpha(reachable ? 1 : 0.85),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
        },
      });
      entityToFeature.set(marker, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      lastFc = fc;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchDroneCoverage failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    setSelectedIncident(): void {
      // Re-paint highlights from the cached features (no refetch).
      if (lastFc && !destroyed) render(lastFc);
    },
    pick(entity: unknown): DroneCoverageFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
